import { useEffect, useRef, type ReactNode } from "react";

// kp-Epic 7 (kennelplanning-migration) — frontend part 1: a generic, PAGE-LOCAL right-side slide-in
// Drawer. This is intentionally NOT a shared @fawkes/ui component (the migration is additive and must
// not touch shared UI). It mirrors the Jmix `kp-drawer` Dialog behaviour: a fixed full-height overlay
// with a panel that transforms in from the right, closes on Esc + backdrop click, caps to the viewport
// (~760px wide), is token-driven, and does basic focus handling (focus the panel on open, restore the
// previously-focused element on close). Children own their own content/layout.

export interface DrawerProps {
  /** Whether the drawer is rendered + slid in. */
  open: boolean;
  /** Close request (Esc / backdrop click / the close button). */
  onClose: () => void;
  /** Accessible label for the dialog region. */
  ariaLabel?: string;
  /** Label for the built-in close button (defaults provided by the caller via i18n). */
  closeLabel?: string;
  children: ReactNode;
}

/** A right-side slide-in panel. Token-driven, Esc/backdrop closeable, basic focus handling. */
export function Drawer({ open, onClose, ariaLabel, closeLabel, children }: DrawerProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  // Remember the element focused before opening so we can restore it on close.
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  // Keep the latest `onClose` in a ref so the focus/Esc effect can read it without depending on it.
  // Call sites pass an unstable arrow (new identity each render); if the effect depended on `onClose`
  // it would re-run on every parent re-render (e.g. toggling a field-settings checkbox) and steal
  // focus back to the panel. Depending only on `[open]` keeps focus where the user put it.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Esc-to-close + focus management. The overlay always mounts (so the slide transition can run); the
  // listeners are only wired while open.
  useEffect(() => {
    if (!open) return;
    restoreFocusRef.current = (document.activeElement as HTMLElement | null) ?? null;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onCloseRef.current();
      }
    };
    document.addEventListener("keydown", onKeyDown, true);
    // Focus the panel so Esc / scroll / tab land inside the drawer.
    const focusTimer = window.setTimeout(() => panelRef.current?.focus(), 0);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      window.clearTimeout(focusTimer);
      // Restore focus to whatever was focused before the drawer opened.
      const el = restoreFocusRef.current;
      if (el && typeof el.focus === "function") el.focus();
    };
  }, [open]);

  return (
    <div
      className={"kp-drawer-overlay" + (open ? " kp-drawer-overlay--open" : "")}
      aria-hidden={open ? undefined : true}
      // Backdrop click (only the overlay itself, not the panel) closes the drawer.
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        className="kp-drawer-panel"
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        tabIndex={-1}
      >
        <button
          type="button"
          className="kp-drawer-close"
          aria-label={closeLabel ?? "Close"}
          title={closeLabel ?? "Close"}
          onClick={onClose}
        >
          ×
        </button>
        {open && children}
      </div>
    </div>
  );
}
